'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import ConsentText from '@/components/public/ConsentText';
import { withUtmSource } from '@/lib/utm';

const COURSE_1_LINK = 'https://buy.stripe.com/14A3cuexdfSX5EA80UbEA05';
const ALLOWED_SOURCES = ['website', 'instagram', 'tiktok', 'linkedin', 'facebook'];

const INTEREST_OPTIONS = [
  'Initial Starter (£49)',
  'Growers Membership',
  'Growers & Governance Membership',
  'Not sure yet',
];

const STAGE_OPTIONS = [
  'Just exploring',
  'Planning to start',
  'Registered with CQC, not yet delivering',
  'Already delivering care',
];

interface RegisterValues {
  fullName: string;
  email: string;
  phone: string;
  interestedIn: string;
  businessStage: string;
  message: string;
  consent: boolean;
  website: string;
}

export default function RegisterForm() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [source, setSource] = useState('website');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterValues>({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      interestedIn: INTEREST_OPTIONS[1],
      businessStage: STAGE_OPTIONS[0],
      message: '',
      consent: false,
      website: '',
    },
  });

  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('source')?.trim().toLowerCase();
    if (param && ALLOWED_SOURCES.includes(param)) setSource(param);
  }, []);

  const onSubmit = async (values: RegisterValues) => {
    setSubmitError(null);

    // Honeypot: bots fill the hidden field. Pretend success, save nothing.
    if (values.website && values.website.trim() !== '') {
      setSent(true);
      return;
    }

    setSubmitting(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.from('membership_interest').insert({
        full_name: values.fullName.trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone.trim() || null,
        interested_in: values.interestedIn,
        business_stage: values.businessStage,
        message: values.message.trim() || null,
        source: withUtmSource(source),
        consent_to_contact: values.consent === true,
      });
      if (error) {
        setSubmitError('Sorry, something went wrong. Please try again or email hello@carebusinessschool.co.uk.');
        return;
      }
      setSent(true);
    } catch {
      setSubmitError('Sorry, something went wrong. Please try again or email hello@carebusinessschool.co.uk.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="card-surface flex flex-col items-center p-6 text-center sm:p-8">
        <CheckCircle2 size={38} className="text-success" />
        <h1 className="mt-3 text-xl font-bold text-card-foreground">Thank you for registering your interest</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The CBS team will contact you to confirm your membership and set up your private portal login.
        </p>
        <div className="mt-5 w-full rounded-xl border border-[#C6A667] bg-[#F8F3E9] p-4">
          <p className="text-sm font-semibold text-[#581C32]">
            Want to start straight away? Course 1 is £49 and you get access immediately
          </p>
          <a
            href={COURSE_1_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mt-3 w-full !bg-[#581C32] !text-[#F8F3E9] sm:w-auto"
          >
            Start Course 1 — £49
          </a>
          <ConsentText className="mt-2 text-[#581C32]" />
        </div>
        <Link href="/" className="btn-outline mt-4">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="card-surface p-6 sm:p-8">
      <h1 className="text-2xl font-bold tracking-tight text-foreground">Register your interest</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Memberships are assigned by the CBS team. Tell us about you and we will be in touch.
      </p>

      {submitError && (
        <div role="alert" className="mt-5 flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-3.5 py-3 text-sm text-destructive">
          <AlertCircle size={17} className="mt-0.5 flex-shrink-0" />
          <span>{submitError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-6 space-y-4">
        <div
          aria-hidden="true"
          style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
        >
          <label htmlFor="reg-website">Website</label>
          <input id="reg-website" type="text" tabIndex={-1} autoComplete="off" {...register('website')} />
        </div>

        <div>
          <label htmlFor="reg-name" className="mb-1.5 block text-sm font-semibold text-foreground">Full name</label>
          <input id="reg-name" type="text" autoComplete="name" className={`field-input ${errors.fullName ? 'field-input-error' : ''}`} {...register('fullName', { required: 'Enter your full name' })} />
          {errors.fullName && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.fullName.message}</p>}
        </div>
        <div>
          <label htmlFor="reg-email" className="mb-1.5 block text-sm font-semibold text-foreground">Email address</label>
          <input
            id="reg-email"
            type="email"
            autoComplete="email"
            className={`field-input ${errors.email ? 'field-input-error' : ''}`}
            {...register('email', {
              required: 'Enter your email address',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.email.message}</p>}
        </div>
        <div>
          <label htmlFor="reg-phone" className="mb-1.5 block text-sm font-semibold text-foreground">Phone number (optional)</label>
          <input id="reg-phone" type="tel" autoComplete="tel" className="field-input" {...register('phone')} />
        </div>
        <div>
          <label htmlFor="reg-membership" className="mb-1.5 block text-sm font-semibold text-foreground">What are you interested in?</label>
          <select id="reg-membership" className="field-input" {...register('interestedIn')}>
            {INTEREST_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="reg-stage" className="mb-1.5 block text-sm font-semibold text-foreground">Where are you with your care business?</label>
          <select id="reg-stage" className="field-input" {...register('businessStage')}>
            {STAGE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="reg-message" className="mb-1.5 block text-sm font-semibold text-foreground">Anything you would like us to know? (optional)</label>
          <textarea id="reg-message" rows={4} className="field-input" {...register('message')} />
        </div>
        <div>
          <label htmlFor="reg-consent" className="flex cursor-pointer items-start gap-3 text-sm text-foreground">
            <input
              id="reg-consent"
              type="checkbox"
              className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-input accent-[#581C32]"
              {...register('consent', { required: 'Please tick the box so we can contact you' })}
            />
            <span>I agree to Care Business School contacting me about membership.</span>
          </label>
          {errors.consent && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.consent.message}</p>}
        </div>
        <button type="submit" disabled={submitting} className="btn-primary w-full !py-3">
          {submitting ? (<><Loader2 size={17} className="animate-spin" /> Sending…</>) : 'Submit registration'}
        </button>
      </form>
      <p className="mt-5 text-center text-sm text-muted-foreground">
        Already a member?{' '}
        <Link href="/login" className="font-semibold text-primary hover:underline">Member Login</Link>
      </p>
    </div>
  );
}
